import {
  GithubIcon,
  IconButton,
  Modal,
  SingleSelectDropdown,
  Text,
  toast,
} from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import {
  addTrainingGroupMember,
  isDuplicateGroupMemberError,
  removeTrainingGroupMember,
  type RegistryGroupRef,
  type TrainingGroup,
} from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import { EmptyState } from "@/shared/ui/emptyState/public";
import type { OrgMemberTableRow } from "./OrgMemberTableRow";
import styles from "./memberMappingModal.module.css";

// 그룹 후보의 조회 상태. 후보는 페이지가 받는다.
export type GroupCandidateStatus = "loading" | "ready" | "error";

export type GroupMappingModalProps = {
  member: OrgMemberTableRow;
  // 추가 드롭다운 후보(레지스트리 그룹 전체).
  groups: readonly TrainingGroup[];
  // 받기 전과 실패는 후보가 비어 있다. "추가할 그룹이 없습니다" 는 `ready` 일 때만 알린다.
  groupsStatus: GroupCandidateStatus;
  onClose: () => void;
  // 매핑 변이가 성공할 때마다 다음 목록을 전달한다. 페이지가 표의 행을 즉시 교체한다.
  // 닫을 때가 아니라 변이마다 호출한다. 모달이 열려 있어도 뒤의 표가 맞아야 하기 때문이다.
  onChanged: (registryGroups: readonly RegistryGroupRef[]) => void;
};

// 한 유저의 Registry 그룹 매핑 편집(a축). 명시(explicit) 매핑만 추가/제거한다. GitHub 팀 자동
// (github_team) 소속은 읽기 전용으로 보여준다. 그 소속은 그룹의 GitHub 팀 매핑에서 관리한다.
// 가입 전에도 편집할 수 있다(github_user_id 키).
export function GroupMappingModal({
  member,
  groups,
  groupsStatus,
  onClose,
  onChanged,
}: GroupMappingModalProps): ReactNode {
  const [refs, setRefs] = useState<RegistryGroupRef[]>([...member.registryGroups]);
  const [busy, setBusy] = useState(false);

  const mapped = new Set(refs.map((ref) => ref.group_uuid));
  const addOptions = groups
    .filter((group) => !mapped.has(group.uuid))
    .map((group) => ({ value: group.uuid, label: group.name }));

  async function handleAdd(groupUuid: string): Promise<void> {
    const group = groups.find((entry) => entry.uuid === groupUuid);
    if (!group || busy) return;
    setBusy(true);
    try {
      await addTrainingGroupMember(group.uuid, {
        github_user_id: member.githubUserId,
        github_login: member.githubLogin,
      });
      const next: RegistryGroupRef[] = [
        ...refs,
        { group_uuid: group.uuid, name: group.name, source: "explicit" },
      ];
      setRefs(next);
      onChanged(next);
    } catch (error) {
      toast.error(
        isDuplicateGroupMemberError(error) ? "이미 매핑된 그룹입니다." : resolveErrorMessage(error),
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(ref: RegistryGroupRef): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      await removeTrainingGroupMember(ref.group_uuid, member.githubUserId);
      const next = refs.filter(
        (entry) => !(entry.group_uuid === ref.group_uuid && entry.source === "explicit"),
      );
      setRefs(next);
      onChanged(next);
    } catch (error) {
      toast.error(resolveErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      aria-labelledby="group-mapping-title"
    >
      <HatModalHeader
        title="그룹을 관리합니다."
        titleId="group-mapping-title"
        close={{ disabled: busy }}
      />
      <div className={styles.body}>
        <Text variant={hatcheryTextModes["caption-bold"]} className={styles.subjectName}>
          {member.githubLogin}
        </Text>
        <div className={styles.panes}>
          <section className={styles.pane} aria-label="GitHub 팀">
            <Text variant={hatcheryTextModes["caption-bold"]} className={styles.paneTitle}>
              GitHub 팀
            </Text>
            {member.githubTeams.length === 0 ? (
              <EmptyState size="sm">소속된 GitHub 팀이 없습니다.</EmptyState>
            ) : (
              <div className={styles.githubTeamList}>
                {member.githubTeams.map((team) => (
                  <span key={team} className={styles.githubTeamItem}>
                    <span className={styles.githubTeamIcon} aria-hidden="true">
                      <GithubIcon />
                    </span>
                    <Text variant={hatcheryTextModes["body-normal"]}>{team}</Text>
                  </span>
                ))}
              </div>
            )}
          </section>
          <section className={styles.pane} aria-label="그룹 매핑">
            <Text variant={hatcheryTextModes["caption-bold"]} className={styles.paneTitle}>
              그룹
            </Text>
            <div className={styles.items} aria-label="현재 매핑">
              {refs.length === 0 ? (
                <EmptyState size="sm">아직 매핑된 그룹이 없습니다.</EmptyState>
              ) : (
                refs.map((ref) => (
                  <span key={`${ref.group_uuid}:${ref.source}`} className={styles.item}>
                    <Text variant={hatcheryTextModes["body-normal"]}>{ref.name}</Text>
                    {ref.source === "explicit" ? (
                      <IconButton
                        text="제거"
                        variant="surface"
                        size="sm"
                        tone="danger"
                        disabled={busy}
                        onClick={() => void handleRemove(ref)}
                      />
                    ) : (
                      <span
                        className={styles.autoBadge}
                        title="GitHub 팀 소속에 따라 자동으로 매핑됩니다."
                      >
                        GitHub 자동
                      </span>
                    )}
                  </span>
                ))
              )}
            </div>
            <SingleSelectDropdown
              mode={hatcheryDropdownModes.default}
              label="그룹 추가"
              value={null}
              options={addOptions}
              placeholder={addPlaceholder(groupsStatus, addOptions.length)}
              disabled={busy || addOptions.length === 0}
              onValueChange={(value) => void handleAdd(value)}
            />
          </section>
        </div>
      </div>
    </Modal>
  );
}

function addPlaceholder(status: GroupCandidateStatus, optionCount: number): string {
  if (status === "loading") {
    return "그룹을 불러오는 중입니다.";
  }
  // 좁은 칸의 드롭다운은 placeholder 를 한 줄로 자른다. 그래서 문장 하나만 둔다. 후보는 이 창을 다시
  // 열 때 페이지가 다시 받는다.
  if (status === "error") {
    return "그룹 목록을 불러오지 못했습니다.";
  }
  return optionCount === 0 ? "추가할 그룹이 없습니다." : "그룹을 선택해 주세요.";
}

function resolveErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "그룹 매핑 요청을 완료하지 못했습니다.";
}
